import { all, put, select, takeLatest } from 'redux-saga/effects';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { push as navigate } from 'connected-react-router';
import {
  crudGetOne,
  crudGetList,
  CRUD_GET_LIST_SUCCESS,
  CRUD_GET_LIST_FAILURE,
  CRUD_CREATE_SUCCESS,
  CRUD_DELETE_SUCCESS,
  CRUD_GET_ONE_FAILURE,
  SIGN_IN_SUCCESS,
} from '@/legacy/redux';

import { PER_PAGE as projectsPerPage } from './loadMoreProjects.saga';

export const SET_WORKSPACE = 'DEC/SET_WORKSPACE';
export const SET_PROJECT = 'DEC/SET_PROJECT';
export const SET_RELEASE = 'DEC/SET_RELEASE';
export const SET_RELEASE_PENDING = 'DEC/SET_RELEASE_PENDING';
export const SET_PROJECT_PENDING = 'DEC/SET_PROJECT_PENDING';
export const SET_CONVERSATION = 'DEC/SET_CONVERSATION';
export const SET_DEC_REPORT = 'DEC/SET_DEC_REPORT';
export const SET_SCOPE = 'DEC/SET_SCOPE';
const SCOPE_STORE_KEY = 'scope';

export const setWorkspace = id => ({
  type: SET_WORKSPACE,
  payload: id,
});

export const setProject = (id, meta) => ({
  type: SET_PROJECT,
  payload: id,
  meta,
});

export const setRelease = id => ({
  type: SET_RELEASE,
  payload: id,
});

export const setReleasePending = pending => ({
  type: SET_RELEASE_PENDING,
  payload: pending,
});
export const setProjectPending = pending => ({
  type: SET_PROJECT_PENDING,
  payload: pending,
});
export const setConversation = id => ({
  type: SET_CONVERSATION,
  payload: id,
});
export const setDecReport = id => ({
  type: SET_DEC_REPORT,
  payload: id,
});

export const setScope = scope => ({
  type: SET_SCOPE,
  payload: scope,
});

export const storeScope = scope => {
  localStorage.setItem(SCOPE_STORE_KEY, JSON.stringify(scope));
};

export const getScope = () => {
  const raw = localStorage.getItem(SCOPE_STORE_KEY);
  return raw ? JSON.parse(raw) : null;
};

const getOne = (model, id) => crudGetOne(model, id, { refresh: false, onFailure: {} });

// load all scope items from store, and get each item separately. this avoids sagas
const loadScope = function* loadScope() {
  const scope = yield select(s => s.scope);
  const actions = [];
  // get each element separately to avoid sagas
  if (scope.workspace !== null && scope.workspace !== undefined) {
    actions.push(put(getOne('workspaces', scope.workspace)));
  }
  if (scope.project !== null && scope.project !== undefined) {
    actions.push(put(getOne('models', scope.project)));
  } else if (scope.workspace) {
    // no project id was persisted - re-derive it from a fresh list fetch rather than
    // trusting a persisted projectPending flag, which may be stuck `true` from a
    // fetch that was still in flight when the page reloaded
    actions.push(put(setProjectPending(true)));
    actions.push(put(getProjects(scope.workspace)));
  }
  if (scope.release !== null && scope.release !== undefined) {
    actions.push(put(getOne('releases', scope.release)));
  } else if (scope.project) {
    // same reasoning as above, but for release
    actions.push(put(setReleasePending(true)));
    actions.push(put(getCurrentRelease(scope.project)));
  }

  yield all(actions);
};

export const initScope = function* initScope() {
  const currentScope = yield select(s => s.scope);

  // we already have a scope, simply do nothing
  if (currentScope.workspace) return;

  const scope = getScope();
  // ensure we have a valid stored scope
  if (scope && typeof scope.workspace === 'string') {
    if (!scope.conversation) scope.conversation = '';
    if (!scope.decReport) scope.decReport = '';

    // set entire scope
    const actions = [
      put(setScope(scope)),
    ];

    // // get each element separately to avoid sagas
    // if (scope.workspace !== null && scope.workspace !== undefined) {
    //   actions.push(put(getOne('workspaces', scope.workspace)));
    // }
    // if (scope.project !== null && scope.project !== undefined) {
    //   actions.push(put(getOne('models', scope.project)));
    // }
    // if (scope.release !== null && scope.release !== undefined) {
    //   actions.push(put(getOne('releases', scope.release)));
    // }

    yield all(actions);
  } else {
    // no scope, load all workspaces to trigger default select sagas
    yield put(getWorkspaces());
  }
};

export const reducer = (previousState = { conversation: '' }, action) => {
  const { type, payload } = action;

  let newState;
  switch (type) {
    case SET_WORKSPACE:
      newState = { ...previousState, workspace: payload };
      break;
    case SET_PROJECT:
      newState = { ...previousState, project: payload };
      break;
    case SET_RELEASE:
      newState = { ...previousState, release: payload };
      break;
    case SET_RELEASE_PENDING:
      newState = { ...previousState, releasePending: payload };
      break;
    case SET_PROJECT_PENDING:
      newState = { ...previousState, projectPending: payload };
      break;
    case SET_CONVERSATION:
      newState = { ...previousState, conversation: payload };
      break;
    case SET_DEC_REPORT:
      newState = { ...previousState, decReport: payload };
      break;
    case SET_SCOPE:
      newState = { ...payload };
      break;
    default:
      newState = previousState;
  }

  if (newState !== previousState && !isEmpty(newState)) {
    // store to local storage
    storeScope(newState);
  }

  // store previous state for use in sagas
  const { _prev, ...prev } = previousState;
  newState._prev = prev;

  return newState;
};

/**
 * @typedef Scope
 * @property {string} [workspace]
 * @property {string} [project]
 * @property {string} [release]
 * @property {boolean} [releasePending]
 * @property {boolean} [projectPending]
 * @property {string} [conversation]
 * @property {string} [decReport]
 * @property { undefined
 *  | { workspace: string; project: string; release: string; conversation: string }
 * } [_prev]
 */

// currently get up to 100 workspaces, need to fix / change how pagination works
const getWorkspaces = () => crudGetList('workspaces', 'default', { pagination: { perPage: 100, page: 1 } });

const getProjects = (workspace, context) => crudGetList('models', workspace, {
  filter: { workspace },
  pagination: { perPage: projectsPerPage, page: 1 },
  sort: { field: 'created', order: 'desc' },
  context,
});

const getCurrentRelease = project => crudGetList('releases', project, {
  filter: { model: project, env: 'test', activeVersionFlag: true },
});

/**
 * Hook to retrive current scope and updates
 * @returns {Scope} The current scope
 */
export const useScope = () => useSelector(s => s.scope);

const workspaceCreated = function* workspaceCreated(action) {
  console.log('loadWorkspaces', action);
  const { payload } = action;
  yield all([
    // set created workspace as desired
    put(setWorkspace(payload.data.id)),
    // load all workspaces to update list
    // TODO change to dispatch the forAsyncLoader
    // put(getWorkspaces()),
    // go to projects
    put(navigate('/projects')),
  ]);
};

const workspaceDeleted = function* workspaceDeleted(action) {
  const { payload, requestPayload } = action;
  const { workspace } = yield select(s => s.scope);
  console.log('workspace deleted', payload, requestPayload, workspace);
  if (requestPayload.id === workspace) {
    // refresh list and trigger sages to get default
    yield put(getWorkspaces());
  }
};

const workspaceLoadFailed = function* workspaceLoadFailed(action) {
  const { payload, requestPayload } = action;
  const { workspace } = yield select(s => s.scope);
  console.log('workspace failed to load', payload, requestPayload, workspace);
  // trigger sages to get default
  yield put(getWorkspaces());
};

const projectCreated = function* projectCreated(action) {
  console.log('project created', action);
  const { data: project } = action.payload;
  const setProjectAction = setProject(
    project.id,
    { skipGetList: true, resetRelease: true },
  );
  const getProjectsAction = getProjects(project.workspace, 'afterCreateProject');

  yield all([
    // set created project as desired
    put(setProjectAction),
    // load all projects to update list
    put(getProjectsAction),
    // go to build
    put(navigate('/build')),
  ]);
};

const workspaceChanged = function* workspaceChanged(action) {
  const { payload: workspace } = action;
  const { _prev, project } = yield select(s => s.scope);

  const actions = [];
  if (workspace !== _prev.workspace) {
    // get the new workspace
    actions.push(put(crudGetOne('workspaces', workspace, { refresh: false, onFailure: {} })));
    if (_prev.workspace) {
      // unset project as it no longer applies to selected workspace
      actions.push(put(setProject(null)));
    }

    // get list of projects so we can select most recent; mark as pending until
    // modelsUpdated/projectLookupFailed clears it
    actions.push(put(setProjectPending(true)));
    actions.push(put(getProjects(workspace)));
  } else if (project) {
    // workspace has not changed, ensure we have selected project
    actions.push(
      put(crudGetOne('models', project, { refresh: false, onFailure: {} })),
    );
  } else {
    // don't have a project, get list so we can select current
    // TODO ideally this should get/set the newest project only
    actions.push(put(setProjectPending(true)));
    actions.push(put(getProjects(workspace)));
  }

  yield all(actions);
};

const projectChanged = function* projectChanged(action) {
  const { payload: project, meta } = action;
  const { resetRelease } = meta ?? { resetRelease: false };
  const { _prev, release } = yield select(s => s.scope);

  if (project === null || project === undefined) {
    // unset release
    yield all([put(setRelease(null)), put(setReleasePending(false))]);
    return;
  }

  const actions = [];

  if (project !== _prev.project) {
    // ensure we have the project we changed to
    actions.push(put(crudGetOne('models', project, { refresh: false, onFailure: {} })));
    // don't auto unset release as it may have already been changed
  } else if (release) {
    // project has not changed, ensure we have selected release
    actions.push(
      put(crudGetOne('releases', release, { refresh: false, onFailure: {} })),
    );
  }

  if (resetRelease) {
    // unset release as it either no longer applies, or we want to default to current
    actions.push(put(setRelease(null)));
  }

  if (resetRelease || !release) {
    // get the current release; mark as pending until releasesUpdated/releaseLookupFailed clears it
    actions.push(put(setReleasePending(true)));
    actions.push(put(getCurrentRelease(project)));
  }

  yield all(actions);
};

const releaseChanged = function* releaseChanged(action) {
  const { payload: release } = action;
  const { _prev } = yield select(s => s.scope);
  // release might be set to null or undefined, only fetch if truthy
  if (release) {
    // don't check if release changed, in case we just want to re-fetch
    // ensure we have the release
    yield put(crudGetOne('releases', release, { refresh: false, onFailure: {} }));
  }
};

const defaultRequired = (id, items) => !id || items.every(i => i.id !== id);

const workspacesUpdated = function* workspacesUpdated(action) {
  const { payload } = action;
  const { workspace } = yield select(s => s.scope);
  const workspaces = payload.data;
  if (workspaces?.length > 0 && defaultRequired(workspace, workspaces)) {
    const def = workspaces.find(w => w.name === 'Default') || workspaces[0];
    yield put(setWorkspace(def.id));
  }
};

const modelsUpdated = function* modelsUpdated(action) {
  const { payload, meta } = action;
  const { workspace, project } = yield select(s => s.scope);
  const projects = payload.data;
  if (meta.location === workspace) {
    if (projects?.length > 0) {
      if (defaultRequired(project, projects)) {
        const def = projects[0].id;
        yield put(setProject(def));
      }
    } else {
      yield put(setProject(null));
    }
    // lookup for the current workspace has settled, whether or not a project was found
    yield put(setProjectPending(false));
  }
};

const releasesUpdated = function* releasesUpdated(action) {
  const { payload, meta } = action;
  const { project, release } = yield select(s => s.scope);
  if (meta.location !== project) return;

  const releases = payload.data;
  if (releases?.length > 0 && defaultRequired(release, releases)) {
    // select the current release, fallback to first
    const def = (releases.find(r => r.activeVersionFlag) || releases[0]).id;
    yield put(setRelease(def));
  }
  // lookup for the current project has settled, whether or not a release was found
  yield put(setReleasePending(false));
};

const releaseLookupFailed = function* releaseLookupFailed(action) {
  const { meta } = action;
  const { project } = yield select(s => s.scope);
  if (meta?.location === project) {
    yield put(setReleasePending(false));
  }
};

const projectLookupFailed = function* projectLookupFailed(action) {
  const { meta } = action;
  const { workspace } = yield select(s => s.scope);
  if (meta?.location === workspace) {
    yield put(setProjectPending(false));
  }
};

const isDefaultList = resource => ({ type, meta }) => {
  return type === CRUD_GET_LIST_SUCCESS &&
    meta?.resource === resource &&
    meta?.id === 'default' &&
    meta.location !== 'forAsyncSelect' &&
    meta.context !== 'afterCreateProject';
};

const hasWorkspaceScope = scope => Boolean(scope?.workspace);

export const saga = function* saga() {
  yield all([
    // need get projects on workspace change
    takeLatest(SET_WORKSPACE, workspaceChanged),
    // need get releases on project change
    takeLatest(SET_PROJECT, projectChanged),
    // ensure we load release on select
    takeLatest(SET_RELEASE, releaseChanged),
    // need set default workspace on receive workspaces
    takeLatest(
      isDefaultList('workspaces'),
      workspacesUpdated,
    ),
    // need set default project on receive projects
    takeLatest(
      isDefaultList('models'),
      modelsUpdated,
    ),
    // need set default release on receive releases
    takeLatest(
      isDefaultList('releases'),
      releasesUpdated,
    ),
    // clear pending state if the release lookup itself fails
    takeLatest(
      action => action.type === CRUD_GET_LIST_FAILURE && action.meta?.resource === 'releases',
      releaseLookupFailed,
    ),
    // clear pending state if the project lookup itself fails
    takeLatest(
      action => action.type === CRUD_GET_LIST_FAILURE && action.meta?.resource === 'models',
      projectLookupFailed,
    ),
    takeLatest(
      action => action.type === CRUD_CREATE_SUCCESS && action.meta?.resource === 'workspaces',
      workspaceCreated,
    ),
    takeLatest(
      action => action.type === CRUD_CREATE_SUCCESS && action.meta?.resource === 'models',
      projectCreated,
    ),
    takeLatest(
      action => action.type === CRUD_DELETE_SUCCESS && action.meta?.resource === 'workspaces',
      workspaceDeleted,
    ),
    takeLatest(
      action => action.type === CRUD_GET_ONE_FAILURE && action.meta?.resource === 'workspaces',
      workspaceLoadFailed,
    ),
    // load scope after sign in success
    takeLatest(SIGN_IN_SUCCESS, initScope),
    // load scope after sign in success
    takeLatest(action => action.type === SET_SCOPE && action.payload === null, function* resetScope() {
      // no scope, load all workspaces to trigger default select sagas
      yield put(getWorkspaces());
    }),
    takeLatest(action => action.type === SET_SCOPE && hasWorkspaceScope(action.payload), loadScope),
  ]);
};
